import React from 'react';
import { McModal } from '@maersk-global/mds-react-wrapper/mc-modal';
import { McButton } from '@maersk-global/mds-react-wrapper';
import './EmailViewModal.scss';

// Minimal escape helper reused from MessagesModal
const escapeHtml = (unsafe) => {
  if (unsafe == null) return '';
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Simple export helper - reuses approach from MessagesModal
const exportMessagesAsPDF = async (caseNumber, messages = []) => {
  if (!messages || messages.length === 0) return;
  if (!caseNumber) return;
  try {
    const parts = [];
    parts.push(`<h2>Case ${escapeHtml(caseNumber)} - Exported Email</h2>`);
    messages.forEach((m) => {
      parts.push('<div class="msg">');
      parts.push(`<div class="hdr"><strong>From:</strong> ${escapeHtml(m.from ?? m.sender ?? 'N/A')}</div>`);
      parts.push(`<div class="hdr"><strong>To:</strong> ${escapeHtml(m.to ?? m.to_recipients ?? 'N/A')}</div>`);
      parts.push(`<div class="hdr"><strong>Subject:</strong> ${escapeHtml(m.subject ?? 'N/A')}</div>`);
      // If body contains HTML tags, include it as-is so formatting (ul/li) is preserved in export.
      const bodyRaw = String(m.body ?? '');
      const looksLikeHtml = /<[^>]+>/.test(bodyRaw);
      const bodyHtml = looksLikeHtml ? bodyRaw : escapeHtml(bodyRaw).replace(/\n/g, '<br/>');
      parts.push(`<div class="body">${bodyHtml}</div>`);
      const received = m.receive_timestamp ? m.receive_timestamp.replace('T', ' ').split('.')[0] : (m.createddate ? m.createddate.replace('T', ' ').split('.')[0] : '');
      parts.push(`<div class="footer"><em>Received: ${escapeHtml(received)}</em></div>`);
      parts.push('</div>');
    });

    // normalize spacing for paragraphs and lists so exported PDF doesn't have large gaps
    const style = '<style>body{font-family: Arial, Helvetica, sans-serif; padding:20px; color:#111} '
      + '.msg{border:1px solid #e6e6e6;padding:12px;border-radius:6px;margin-bottom:12px;} '
      + '.hdr{font-size:13px;color:#333;margin-bottom:6px;} '
      + '.body{font-size:12px;color:#444;white-space:normal;margin-top:6px;line-height:1.4;} '
      + '.body p{margin:0 0 10px 0;padding:0;} '
      + '.body ul{margin:0 0 10px 20px;padding-left:20px;} '
      + '.body li{margin:0 0 6px 0;padding:0;} '
      + '.footer{font-size:11px;color:#777;margin-top:8px;}</style>';
    const fullHtml = '<!doctype html><html><head><meta charset="utf-8"><title>Exported Email</title>' + style + '</head><body>' + parts.join('') + '</body></html>';

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-10000px';
    iframe.style.top = '0';
    iframe.style.width = '800px';
    iframe.style.height = '1000px';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.srcdoc = fullHtml;
    document.body.appendChild(iframe);

    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Iframe load timed out')), 10000);
      iframe.onload = () => { clearTimeout(timeout); resolve(); };
    });

    // Fetch and inject html2pdf bundle (cached on window if previously loaded)
    let bundleCode = window.__html2pdf_bundle_code;
    if (!bundleCode) {
      const bundleUrl = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      const resp = await fetch(bundleUrl);
      if (!resp.ok) throw new Error(`Failed to fetch html2pdf bundle: ${resp.status}`);
      bundleCode = await resp.text();
      try { window.__html2pdf_bundle_code = bundleCode; } catch (e) { /* ignore */ }
    }

    const iDoc = iframe.contentDocument || iframe.contentWindow.document;
    const script = iDoc.createElement('script');
    script.type = 'text/javascript';
    script.text = bundleCode;
    iDoc.head.appendChild(script);

    // Wait for html2pdf to initialize inside iframe
    await new Promise((resolve, reject) => {
      const maxWait = 5000; const start = Date.now();
      const check = () => {
        try { if (iframe.contentWindow && typeof iframe.contentWindow.html2pdf === 'function') return resolve(); } catch (e) {}
        if (Date.now() - start > maxWait) return reject(new Error('html2pdf did not initialize inside iframe'));
        setTimeout(check, 50);
      };
      check();
    });

    const opt = { margin: 10, filename: `Case-${caseNumber}-Email.pdf`, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2 }, jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' } };

    const blobPromise = new Promise((resolve, reject) => {
      let settled = false;
      const onMessage = (e) => {
        if (e.source !== iframe.contentWindow) return;
        const data = e.data || {};
        if (data && data.type === 'html2pdf:blob' && data.blob) {
          if (settled) return; settled = true; window.removeEventListener('message', onMessage); resolve(data.blob);
        } else if (data && data.type === 'html2pdf:error') {
          if (settled) return; settled = true; window.removeEventListener('message', onMessage); reject(new Error(data.message || 'Unknown error in iframe'));
        }
      };
      window.addEventListener('message', onMessage);
      setTimeout(() => { if (settled) return; settled = true; window.removeEventListener('message', onMessage); reject(new Error('Timed out waiting for PDF blob from iframe')); }, 20000);
    });

    const genScript = iDoc.createElement('script');
    genScript.type = 'text/javascript';
    genScript.text = "(function(){try{var opt = " + JSON.stringify(opt) + "; html2pdf().from(document.body).set(opt).toPdf().get('pdf').then(function(pdf){ try{ var blob = pdf.output('blob'); parent.postMessage({type:'html2pdf:blob', blob: blob}, '*'); }catch(e){ parent.postMessage({type:'html2pdf:error', message: e && e.message ? e.message : String(e)}, '*'); } }).catch(function(err){ parent.postMessage({type:'html2pdf:error', message: err && err.message ? err.message : String(err)}, '*'); }); }catch(e){ parent.postMessage({type:'html2pdf:error', message: e && e.message ? e.message : String(e)}, '*'); }})();";
    iDoc.body.appendChild(genScript);

    const blob = await blobPromise;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = opt.filename || `Case-${caseNumber}-Email.pdf`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    try { document.body.removeChild(iframe); } catch (e) { /* ignore */ }
    return;
  } catch (err) {
    console.error('Export Email failed', err);
    alert('Failed to export email: ' + (err.message || String(err)));
  }
};

const EmailViewModal = ({ open, onClose, caseNumber, initialData }) => {
  const effectiveData = initialData || null;
  const messages = effectiveData?.messages || [];

  const renderBodyNode = (body) => {
    const content = body ?? '';
    // detect if the body looks like HTML (contains tags)
    const looksLikeHtml = /<[^>]+>/.test(content);
    const commonStyle = { fontSize: '12px', color: '#666', marginTop: '6px', whiteSpace: 'pre-wrap' };
    if (looksLikeHtml) {
      // Render server-provided HTML. We assume this content is trusted/internal.
      return <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }} dangerouslySetInnerHTML={{ __html: content }} />;
    }
    // Plain text: show safely
    return <div style={commonStyle}>{String(content)}</div>;
  };

  return (
    <McModal
      heading={`Email - ${caseNumber || ''}`}
      closed={() => { if (onClose) onClose(); }}
      open={open}
      width="50vw"
      height="60vh"
    >
      <div style={{ padding: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>{effectiveData?.casenumber ? `Case Number - ${effectiveData.casenumber}` : ''}</div>
          <div>
            {messages.length > 0 && (
              <McButton
                label={`Export ${messages.length > 1 ? 'Emails' : 'Email'}`}
                appearance="secondary"
                click={async () => {
                  try {
                    await exportMessagesAsPDF(effectiveData?.casenumber || caseNumber, messages);
                    if (typeof onClose === 'function') onClose();
                  } catch (err) {
                    console.error('Export failed', err);
                  }
                }}
              />
            )}
          </div>
        </div>

        {effectiveData && effectiveData.error ? (
          <div style={{ color: 'red' }}>{effectiveData.error}</div>
        ) : messages && messages.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {messages.map((m, i) => (
              <div key={i} style={{ border: '1px solid #e6e6e6', padding: '8px', borderRadius: '4px', overflowY: 'auto', maxHeight: '220px' }}>
                <div style={{ fontSize: '12px', color: '#333' }}><strong>From:</strong> {m.from ?? m.sender ?? 'N/A'}</div>
                <div style={{ fontSize: '12px', color: '#333' }}><strong>To:</strong> {m.to ?? m.to_recipients ?? 'N/A'}</div>
                <div style={{ fontSize: '12px', color: '#333' }}><strong>Subject:</strong> {m.subject ?? 'N/A'}</div>
                <div style={{ fontSize: '12px'}}>{renderBodyNode(m.body)}</div>
                <div style={{ fontSize: '11px', color: '#999', marginTop: '6px' }}><em>Received At: {m.receivedtime ? m.receivedtime.replace('T', ' ').split('.')[0] : (m.createddate ? m.createddate.replace('T', ' ').split('.')[0] : '')}</em></div>
                {/* <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}><strong>Inbound Email ID:</strong> {m.inbound_email_id ?? ''}</div> */}
              </div>
            ))}
          </div>
        ) : (
          <div>No email details available.</div>
        )}
      </div>
    </McModal>
  );
};

export default EmailViewModal;
